import type {ForwardedRef, MouseEventHandler, ReactElement} from 'react';
import React, {useMemo} from 'react';
import {getCircularSocialIconURLByType} from '@Libraries/social-media-platform.library';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {getDefaultDisplayURL, swapToDefaultDisplayURL} from '@Components/social-media/account.vo';
import {ClickableDiv} from '@Components/clickable-div';
import {SocialPlatform} from '@Components/social-media/social-media.types';
import {PlatformMediaType} from '@Components/social-media/post.vo';
import type {SocialProfileIconProps} from './social-profile-icon.types';
import {SocialProfileIconSize} from './social-profile-icon.types';
import styles from './social-profile-icon.module.scss';
import {Icon} from '@/components/icon-v2';

function SocialProfileIcon(
  {
    account,
    onClick,
    size,
    className = '',
    isActive = false,
    isDisabled = false,
    showPlatformIcon = true,
    showHoverOverlay = true,
    isError = false,
    platformMediaType = PlatformMediaType.POST,
    onRemoveClick,
  }: SocialProfileIconProps,
  ref: ForwardedRef<HTMLDivElement>
): ReactElement {
  const getClassesForSize = (): string => {
    switch (size) {
      case SocialProfileIconSize.MEDIUM:
        return styles.medium;
      case SocialProfileIconSize.LARGE:
        return styles.large;
      default:
        return styles.medium;
    }
  };

  const getDisabledClass = (): string => {
    return isDisabled ? styles.disabled : '';
  };

  const getPlatformSpecificImageClasses = (): string => {
    return account.type === SocialPlatform.YOUTUBE ? styles.shortHeight : '';
  };

  const getRemoveClickHandler = (): MouseEventHandler | undefined => {
    if (!onRemoveClick) {
      return undefined;
    }

    return (event): void => {
      event.stopPropagation();
      onRemoveClick();
    };
  };

  const getOverlayElements = (): ReactElement => {
    return (
      <>
        <Icon className={styles.iconClose} icon="icon-close" size={IconSize.SIZE_ICON_20} />
        <div className={styles.overlay} aria-hidden="true" />
      </>
    );
  };

  const getRemoveAccountButton = (): ReactElement | null => {
    if (!onRemoveClick) {
      return null;
    }

    return (
      <Icon
        className={styles.removeAccountButton}
        icon="icon-close"
        size={IconSize.SIZE_ICON_12}
        type={IconType.NONE}
        shape={IconShape.NONE}
        onClick={getRemoveClickHandler()}
        isUsedAsButton
        ariaLabel={window.i18next.t('pmwjs_remove_account')}
      />
    );
  };

  const socialPlatformIconElement = useMemo((): null | ReactElement => {
    if (showPlatformIcon) {
      return (
        <img
          className={`${styles.socialIcon} ${getPlatformSpecificImageClasses()} ${getDisabledClass()}`}
          src={getCircularSocialIconURLByType(account.type, platformMediaType)}
          alt=""
          aria-hidden="true"
        />
      );
    }

    return null;
  }, [showPlatformIcon, isDisabled]);

  const isClickable = onClick !== undefined;

  const displayImageElement = useMemo((): ReactElement => {
    return (
      <div className={`${styles.socialProfileContainer} ${isError ? styles.errorOutline : ''} ${getClassesForSize()}`}>
        <img
          className={`${styles.socialProfile} ${getDisabledClass()} flex-center`}
          src={account.displayURL ?? getDefaultDisplayURL()}
          alt={window.i18next.t('pmwjs_socialmedia_profile_picture', {socialMediaName: account.name})}
          onError={swapToDefaultDisplayURL}
        />
      </div>
    );
  }, [isDisabled, isError]);

  const getActiveBorderClasses = (): string => {
    if (!isActive) {
      return '';
    }

    if (isError) {
      return styles.activeErrorStyles;
    }

    return styles.activeStyles;
  };

  return (
    <ClickableDiv
      className={`${getClassesForSize()} ${className} ${styles.socialProfileIcon} ${isClickable ? styles.clickable : ''}  ${getActiveBorderClasses()}`}
      onClick={(): void => {
        if (onClick) {
          onClick();
        }
      }}
      ref={ref}
    >
      {displayImageElement}
      {socialPlatformIconElement}
      {isClickable && showHoverOverlay ? getOverlayElements() : null}
      {getRemoveAccountButton()}
    </ClickableDiv>
  );
}

export default React.memo(React.forwardRef(SocialProfileIcon));
